import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FiSend } from "react-icons/fi";
import type { Row as TanStack } from "@tanstack/react-table";
import type { ReportMetricRow } from "@core/components/ReportTreeTable";
import type { DepartmentReportMediaItem } from "@core/reports/api/types";
import { parseReportViewSearch } from "@core/reports/constants/reportSpaQueryParams";
import { useReportPage } from "@core/reports/hooks/useReportPage";
import ReportPageTemplate, {
  type TemplatePreviewContext,
} from "@core/reports/components/ReportPageTemplate";
import RequestReviewModal from "@core/reports/components/RequestReviewModal";
import TemplateRowPreviewButton from "@core/reports/components/TemplateRowPreviewButton";
import ChildMediaPopover from "@core/reports/components/ChildMediaPopover";
import {
  canRequestChildDepartmentReportReview,
  isAncestorOpenForChildReview,
} from "@core/reports/utils/reportWorkflow";
import { requestReportReviewAsync } from "@core/reports/api/requestReportReview";

const iconBtnStyle = (disabled: boolean): React.CSSProperties => ({
  width: 28,
  height: 28,
  border: "1px solid var(--ntcog-border)",
  borderRadius: "var(--ntcog-radius-sm)",
  background: disabled ? "var(--ntcog-surface)" : "var(--ntcog-input)",
  cursor: disabled ? "not-allowed" : "pointer",
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
});

export default function DistrictReportPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const [searchParams] = useSearchParams();
  const numericDistrictId = Number(districtId) || 0;
  const { departmentId, reportTagId } = parseReportViewSearch(searchParams);

  const report = useReportPage({
    scope: "district",
    entityId: numericDistrictId,
    departmentId,
    reportTagId,
  });
  const {
    canLoad,
    departmentReportId,
    statusName,
    load,
    loadDepartmentReport,
    permissionFlags,
    permissionsLoading,
  } = report;

  const showRowReview = permissionFlags.showReview && !permissionsLoading;
  const parentOpenForChildReview = isAncestorOpenForChildReview(departmentReportId, statusName);

  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [reviewChurch, setReviewChurch] = React.useState<{
    id: number;
    name: string;
    reportId: number | null;
  } | null>(null);
  const [requestingReview, setRequestingReview] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);

  const handleConfirmReview = async ({ reason }: { reason: string }) => {
    if (!canLoad) return;
    if (!reviewChurch?.reportId || reviewChurch.id <= 0) {
      setReviewError("Missing department report id for the selected church.");
      return;
    }
    setRequestingReview(true);
    setReviewError(null);
    try {
      const result = await requestReportReviewAsync({
        scope: "church",
        churchId: reviewChurch.id,
        departmentId,
        departmentReportId: reviewChurch.reportId,
        notes: reason.slice(0, 255),
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to request review.");
      setShowReviewModal(false);
      await load();
      await loadDepartmentReport();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setRequestingReview(false);
    }
  };

  const renderRowName = React.useCallback(
    (row: TanStack<ReportMetricRow>, ctx: TemplatePreviewContext) => {
      const original: any = row.original ?? {};
      const name = String(original.church_name ?? original.name ?? "").trim() || "Church";
      const churchId = Number(original.church_id ?? original.__meta?.church_id ?? 0) || 0;
      const childReportId = Number(original.department_report_id ?? 0) || null;
      const childMedia: DepartmentReportMediaItem[] = Array.isArray(original.media)
        ? (original.media as DepartmentReportMediaItem[])
        : [];
      const rowType = original.__rowType as string | undefined;
      const templateId = Number(original.template_id ?? original.__meta?.template_id ?? 0) || 0;
      const isTemplateLeaf =
        rowType === "template" ||
        rowType === "sub_group" ||
        (templateId > 0 && row.depth > 0);

      const childStatus =
        original.church_report_status_name ?? original.report_status_name ?? null;
      const childStatusAllowsReview =
        !childStatus || canRequestChildDepartmentReportReview(childStatus);
      const showButton =
        !isTemplateLeaf &&
        row.depth === 0 &&
        showRowReview &&
        parentOpenForChildReview &&
        !!childReportId &&
        churchId > 0 &&
        childStatusAllowsReview;
      const disabled = requestingReview || !canLoad || !childReportId || churchId <= 0;

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "nowrap",
            minWidth: 0,
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
            {isTemplateLeaf ? (
              <TemplateRowPreviewButton
                onPreview={() => ctx.onPreview({ name: String(original.name ?? "Template"), row: original })}
              />
            ) : null}
            {showButton ? (
              <button
                type="button"
                title="Request review"
                disabled={disabled}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setReviewChurch({ id: churchId, name, reportId: childReportId });
                  setReviewError(null);
                  setShowReviewModal(true);
                }}
                style={iconBtnStyle(disabled)}
              >
                <FiSend />
              </button>
            ) : null}
          </div>

          <span
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              fontWeight: showButton ? 700 : 600,
              whiteSpace: "normal",
              overflowWrap: "break-word",
              lineHeight: 1.25,
            }}
            title={name}
          >
            {name}
          </span>

          {churchId > 0 ? (
            <span style={{ flex: "0 0 auto" }}>
              <ChildMediaPopover
                scope="church"
                entityId={churchId}
                departmentId={departmentId}
                departmentReportId={childReportId}
                media={childMedia}
              />
            </span>
          ) : null}
        </div>
      );
    },
    [
      requestingReview,
      canLoad,
      departmentId,
      showRowReview,
      parentOpenForChildReview,
    ]
  );

  return (
    <ReportPageTemplate report={report} scopeNoun="district" renderRowName={renderRowName}>
      <RequestReviewModal
        show={showReviewModal}
        onHide={() => {
          if (!requestingReview) setShowReviewModal(false);
        }}
        subject={
          <>
            You are requesting review for:{" "}
            <span className="fw-semibold">{reviewChurch?.name ?? "Church"}</span>
          </>
        }
        busy={requestingReview}
        error={reviewError}
        onConfirm={handleConfirmReview}
      />
    </ReportPageTemplate>
  );
}
